import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import './DemandReport.css';

import searchIcon from '../assets/Icon/search_icon.svg';
import Multiselect from 'multiselect-react-dropdown';
import { apiService } from '../services/apiService';
import { hasPermission, PERMISSION_CODES, filterDataByUserAccess } from '../utils/permissionUtils';

const DemandReport = () => {
  const { user } = useSelector(state => state.auth);
  const [demandData, setDemandData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regions, setRegions] = useState([]);
  const [interchangeNumbers, setInterchangeNumbers] = useState([]);
  const [partTypes, setPartTypes] = useState([]);
  
  // Selected values state
  const [selectedRegions, setSelectedRegions] = useState(['All']);
  const [selectedPartTypes, setSelectedPartTypes] = useState(['All']);
  const [selectedInterchangeNumbers, setSelectedInterchangeNumbers] = useState(['All']);
  
  // Calculate default dates once during component initialization
  const defaultStartDate = React.useMemo(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const year = firstDay.getFullYear();
    const month = String(firstDay.getMonth() + 1).padStart(2, '0');
    const day = String(firstDay.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const defaultEndDate = React.useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);
  
  
  
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  
  // Applied filters state (for search button functionality)
  const [appliedFilters, setAppliedFilters] = useState({
    partTypes: ['All'],
    regions: ['All'],
    interchangeNumbers: ['All'],
    startDate: defaultStartDate,
    endDate: defaultEndDate
  });
  
  // Permission check
  const canViewDemandReport = hasPermission(user, PERMISSION_CODES.DEMAND_REPORT);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100); // Increased from 10 to 100
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Data fetching state
  const [allData, setAllData] = useState([]); // Store all fetched data
  const [displayData, setDisplayData] = useState([]); // Data to display on current page
  
  // API response statistics state
  const [apiStats, setApiStats] = useState({
    totalNoOfSearch: 0,
    totalFound: 0,
    totalNotFound: 0,
    totalQuantityOfSales: 0
  });

  // Get user from session storage
  const getUserFromSession = () => {
    try {
      const userData = sessionStorage.getItem('user');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data from session:', error);
      return null;
    }
  };

  // Function to map part type codes to display names
  const getPartTypeDisplayName = (partTypeCode) => {
    const partTypeMap = {
      'AA': 'ENGINE',
      'BA': 'TRANSMISSION'
    };
    return partTypeMap[partTypeCode] || partTypeCode;
  };

  // Function to get display names for dropdown options
  const getPartTypeOptions = () => {
    if (partTypes.length === 0) return ['All', 'ENGINE', 'TRANSMISSION'];
    
    return partTypes.map(partType => {
      if (partType === 'All') return 'All';
      return getPartTypeDisplayName(partType);
    });
  };

  // Function to get actual part type code from display name
  const getPartTypeCodeFromDisplay = (displayName) => {
    const reverseMap = {
      'ENGINE': 'AA',
      'TRANSMISSION': 'BA'
    };
    return reverseMap[displayName] || displayName;
  };

  // Fetch main demand data using GetfilterDataForDemand API
  useEffect(() => {
    const fetchMainDemandData = async () => {
      try {
        setLoading(true);
        
        // Fetch data using GetfilterDataForDemand API (no parameters)
        const response = await apiService.GetfilterDataForDemand();
        
        if (response && response.data) {
          // Handle the API response structure properly
          const responseData = response.data;
          let combinedData = [];
          
          // Check if demandReports exists and is not null
          if (responseData.demandReports && Array.isArray(responseData.demandReports)) {
            combinedData = responseData.demandReports;
          } else if (Array.isArray(responseData)) {
            // If response.data is directly an array
            combinedData = responseData;
          } else {
            // If no valid data, set empty array
            combinedData = [];
          }
          
          setDemandData(combinedData);
          setAllData(combinedData);
          setDisplayData(combinedData);
          
          // Don't set statistics from initial data - wait for GetAllDemandData
          // This prevents static values from showing in the UI
          
          // Set total records from response
          const totalRecordsCount = responseData.totalNoOfSearch || responseData.totalFound || combinedData.length || 0;
          setTotalRecords(totalRecordsCount);
          
          // Extract unique values for dropdowns from new API response format
          if (combinedData.length > 0) {
            const uniquePartTypes = ['All', ...new Set(combinedData.map(item => item.part_type).filter(Boolean))];
            const uniqueICNumbers = ['All', ...new Set(combinedData.map(item => item.iC_number?.trim()).filter(Boolean))];
            const uniqueRegions = ['All', ...new Set(combinedData.map(item => item.region?.trim()).filter(Boolean))];

            setPartTypes(uniquePartTypes);
            setInterchangeNumbers(uniqueICNumbers);
            setRegions(uniqueRegions);
          } else {
            // Set default values if no data
            setPartTypes(['All', 'ENGINE', 'TRANSMISSION']);
            setInterchangeNumbers(['All', '10007', '10008', '10009']);
            setRegions(['All', 'MAR', 'NAR', 'EUR']);
          }
        }
        
      } catch (error) {
        console.error('Error fetching main demand data:', error);
        // Set default values on error
        setRegions(['All', 'MAR', 'NAR', 'EUR']);
        setInterchangeNumbers(['All', '10007', '10008', '10009']);
        setPartTypes(['All', 'ENGINE', 'TRANSMISSION']);
      } finally {
        setLoading(false);
      }
    };

    fetchMainDemandData();
  }, []);

  // Fetch additional demand data using GetAllDemandData API with default values
  useEffect(() => {
    const fetchAdditionalDemandData = async () => {
      try {
        // Prepare default values for API call
        const defaultDemandReportInput = {
          partType: [],
          IC_number: [],
          regions: [],
          start_date: defaultStartDate,
          end_date: defaultEndDate
        };
        
        // Fetch data using GetAllDemandData API with default values
        const response = await apiService.GetAllDemandData(defaultDemandReportInput);
        
        if (response && response.data) {
          console.log('GetAllDemandData response:', response.data);
          
          // Override the statistics with the new API response data
          const newApiStats = {
            totalNoOfSearch: response.data.totalNoOfSearch || 0,
            totalFound: response.data.totalFound || 0,
            totalNotFound: response.data.totalNotFound || 0,
            totalQuantityOfSales: response.data.totalQuantityOfSales || 0
          };
          setApiStats(newApiStats);
          
          // Update total records from new API response
          const newTotalRecords = response.data.totalNoOfSearch || 0;
          setTotalRecords(newTotalRecords);
        }
        
      } catch (error) {
        console.error('Error fetching additional demand data:', error);
        // Handle error for additional data separately
      }
    };

    fetchAdditionalDemandData();
  }, [defaultStartDate, defaultEndDate]);

  // Update statistics when applied filters change
  useEffect(() => {
    // This will trigger a re-render with updated statistics
    // when applied filters change (after search button click)
  }, [appliedFilters]);



  // Function to update IC Number dropdowns based on selected Part Types
  const updateDropdownsBasedOnPartTypes = useCallback((selectedPartTypes) => {
    if (!demandData.length) {
      console.log('updateDropdownsBasedOnPartTypes: No demand data available');
      return;
    }

    if (selectedPartTypes.includes('All')) {
      // If "All" is selected, show all IC numbers
      const uniqueICNumbers = ['All', ...new Set(demandData.map(item => item.iC_number).filter(Boolean))];
      console.log('updateDropdownsBasedOnPartTypes: Setting all IC numbers:', uniqueICNumbers);
      setInterchangeNumbers(uniqueICNumbers);
    } else {
      // Filter data by ALL selected part types (using actual codes)
      const filteredByPartTypes = demandData.filter(item => 
        selectedPartTypes.some(partType => item.part_type === partType)
      );
      
      // Extract unique IC numbers from filtered data
      const uniqueICNumbers = ['All', ...new Set(filteredByPartTypes.map(item => item.iC_number?.trim()).filter(Boolean))];
      console.log('updateDropdownsBasedOnPartTypes: Setting filtered IC numbers:', uniqueICNumbers);
      
      setInterchangeNumbers(uniqueICNumbers);
      
      // Don't reset selections automatically - let user choose
    }
  }, [demandData]);

  // Function to update IC Number dropdown based on selected Region
  const updateICNumbersBasedOnRegion = useCallback((selectedRegions) => {
    if (!demandData.length) {
      console.log('updateICNumbersBasedOnRegion: No demand data available');
      return;
    }

    if (selectedRegions.includes('All')) {
      // If "All" is selected, show IC numbers based on current part types filter
      let filteredData = demandData;
      
      if (!selectedPartTypes.includes('All')) {
        // Convert display names to codes for filtering
        const partTypeCodes = selectedPartTypes.map(displayName => getPartTypeCodeFromDisplay(displayName));
        filteredData = demandData.filter(item => 
          partTypeCodes.some(partType => item.part_type === partType)
        );
      }
      
      const uniqueICNumbers = ['All', ...new Set(filteredData.map(item => item.iC_number?.trim()).filter(Boolean))];
      console.log('updateICNumbersBasedOnRegion: Setting all IC numbers based on regions:', uniqueICNumbers);
      setInterchangeNumbers(uniqueICNumbers);
    } else {
      // Filter data by selected regions and part types
      let filteredData = demandData.filter(item => 
        selectedRegions.some(region => item.region === region)
      );
      
      if (!selectedPartTypes.includes('All')) {
        // Convert display names to codes for filtering
        const partTypeCodes = selectedPartTypes.map(displayName => getPartTypeCodeFromDisplay(displayName));
        filteredData = filteredData.filter(item => 
          partTypeCodes.some(partType => item.part_type === partType)
        );
      }
      
      const uniqueICNumbers = ['All', ...new Set(filteredData.map(item => item.iC_number?.trim()).filter(Boolean))];
      console.log('updateICNumbersBasedOnRegion: Setting filtered IC numbers based on regions:', uniqueICNumbers);
      
      setInterchangeNumbers(uniqueICNumbers);
      
      // Don't reset IC number selection automatically - let user choose
    }
  }, [demandData, selectedPartTypes]);









  // Handle dropdown selection with "All" logic
  const handleSelect = useCallback((selectedList, selectedItem, setSelectedValues) => {
    console.log('Selected:', selectedList);
    
    // If "All" is selected, clear other selections
    if (selectedItem === 'All') {
      setSelectedValues(['All']);
      
      // Don't update dropdowns automatically - keep them unchanged
    } else {
      // If a specific item is selected, remove "All" from the list
      const filteredList = selectedList.filter(item => item !== 'All');
      setSelectedValues(filteredList);
      
      // Don't update dropdowns automatically - keep them unchanged
    }
  }, []);

  // Handle dropdown removal with "All" logic
  const handleRemove = useCallback((selectedList, removedItem, setSelectedValues, options) => {
    console.log('Removed:', selectedList);
    
    // If "All" is being removed, do nothing (keep "All" selected)
    if (removedItem === 'All') {
      return;
    }
    
    // If no items are left after removal, select "All"
    if (selectedList.length === 0) {
      setSelectedValues(['All']);
      
      // Don't update dropdowns automatically - keep them unchanged
    } else {
      setSelectedValues(selectedList);
      
      // Don't update dropdowns automatically - keep them unchanged
    }
  }, []);

  const onSelect = (selectedList, selectedItem) => {
  };

  const onRemove = (selectedList, removedItem) => {
  };

  // Handle search button click
  const handleSearch = useCallback(async () => {
    // Validate date range
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      alert('End Date cannot be after Start Date');
      return;
    }
    
    try {
      setLoading(true);
      
      // Prepare filter values for API call
      const demandReportInput = {
        partType: selectedPartTypes.includes('All') ? [] : selectedPartTypes.map(displayName => getPartTypeCodeFromDisplay(displayName)),
        IC_number: selectedInterchangeNumbers.includes('All') ? [] : selectedInterchangeNumbers.map(ic => ic.trim()),
        regions: selectedRegions.includes('All') ? [] : selectedRegions.map(r => r.trim()),
        start_date: startDate,
        end_date: endDate
      };
      
      // Fetch filtered data using GetAllDemandData API with selected filters
      const response = await apiService.GetAllDemandData(demandReportInput);
      
      if (response && response.data) {
        // Handle the API response structure properly
        const responseData = response.data;
        let filteredData = [];
        
        // Check if demandReports exists and is not null
        if (responseData.demandReports && Array.isArray(responseData.demandReports)) {
          filteredData = responseData.demandReports;
        } else if (Array.isArray(responseData)) {
          // If response.data is directly an array
          filteredData = responseData;
        } else {
          // If no valid data, set empty array
          filteredData = [];
        }
        
        setDemandData(filteredData);
        setAllData(filteredData);
        setDisplayData(filteredData);
        
        // Extract and store API statistics
        const apiStatistics = {
          totalNoOfSearch: responseData.totalNoOfSearch || 0,
          totalFound: responseData.totalFound || 0,
          totalNotFound: responseData.totalNotFound || 0,
          totalQuantityOfSales: responseData.totalQuantityOfSales || 0
        };
        setApiStats(apiStatistics);
        
        // Set total records from response
        const totalRecordsCount = responseData.totalNoOfSearch || responseData.totalFound || filteredData.length || 0;
        setTotalRecords(totalRecordsCount);
        
        // Update applied filters
        setAppliedFilters({
          partTypes: selectedPartTypes,
          regions: selectedRegions,
          interchangeNumbers: selectedInterchangeNumbers,
          startDate: startDate,
          endDate: endDate
        });
        
        // Reset to first page when applying new filters
        setCurrentPage(1);
      }
      
    } catch (error) {
      console.error('Error fetching filtered demand data:', error);
      alert('Error fetching filtered data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedPartTypes, selectedRegions, selectedInterchangeNumbers]);

  // Pagination handlers
  const handlePageChange = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
    
    // Since we now have all data loaded, we just need to change the page
    // No need to load more data from server

  }, []);

  const handleItemsPerPageChange = useCallback((event) => {
    setItemsPerPage(parseInt(event.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  }, []);

  // Render pagination controls (matching All Interchanges style)
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <button
        key="prev"
        className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        ←
      </button>
    );

    // First page
    if (startPage > 1) {
      pages.push(
        <button
          key="1"
          className="pagination-btn"
          onClick={() => handlePageChange(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          className="pagination-btn"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    pages.push(
      <button
        key="next"
        className={`pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`}
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        →
      </button>
    );

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          <div className="d-flex align-items-center">
            <span>Show:</span>
            <select 
              className="form-select mx-2" 
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>entries</span>
          </div>
          <span className="pagination-info-text">
            Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
          </span>
        </div>
        <div className="pagination-controls">
          {pages}
        </div>
      </div>
    );
  };

    // Get filtered data based on applied filters
  const getCurrentFilteredData = () => {
      // Safety check: ensure demandData is an array
      if (!demandData || !Array.isArray(demandData)) {
        return [];
      }
      
      let filteredData = demandData;
    
    // Filter by part type
    if (!appliedFilters.partTypes.includes('All')) {
      filteredData = filteredData.filter(item => 
        appliedFilters.partTypes.some(partType => item.part_type === partType)
      );
    }
    
    // Filter by region
    if (!appliedFilters.regions.includes('All')) {
      filteredData = filteredData.filter(item => 
        appliedFilters.regions.some(region => item.region === region)
      );
    }
    
    // Filter by IC number
    if (!appliedFilters.interchangeNumbers.includes('All')) {
      filteredData = filteredData.filter(item => 
        appliedFilters.interchangeNumbers.some(icNumber => item.iC_number === icNumber)
      );
    }

    // Filter by start date
    if (appliedFilters.startDate) {
      filteredData = filteredData.filter(item => {
        const itemDate = item.created_date || item.search_date || item.sales_date || item.approved_date || item.date;
        return itemDate && new Date(itemDate) >= new Date(appliedFilters.startDate);
      });
    }

    // Filter by end date
    if (appliedFilters.endDate) {
      filteredData = filteredData.filter(item => {
        const itemDate = item.created_date || item.search_date || item.sales_date || item.approved_date || item.date;
        return itemDate && new Date(itemDate) <= new Date(appliedFilters.endDate);
      });
    }
    
    return filteredData;
  };

  // Use useMemo to prevent infinite re-renders
  const filteredData = useMemo(() => getCurrentFilteredData(), [demandData, appliedFilters]);

  // Pagination calculations
  const totalItems = filteredData.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = useMemo(() => filteredData.slice(startIndex, endIndex), [filteredData, startIndex, endIndex]);
  
  // Update total pages when data changes
  useEffect(() => {
    const calculatedTotalPages = Math.ceil(totalItems / itemsPerPage);
    setTotalPages(calculatedTotalPages);
  }, [totalItems, itemsPerPage]);

  // Reset to first page when applied filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilters]);

  // Ensure IC Number dropdown always has options
  useEffect(() => {
    if (interchangeNumbers.length === 0 || (interchangeNumbers.length === 1 && interchangeNumbers[0] === 'All')) {
      console.log('Ensuring IC Number dropdown has options');
      // If no options or only 'All', set default options
      setInterchangeNumbers(['All', '10007', '10008', '10009']);
    }
  }, [interchangeNumbers]);

  // Calculate statistics based on API response data only
  const calculateStatistics = () => {
    // Only use API response data - no fallback to calculated values
    if (apiStats.totalNoOfSearch >= 0) {
      // Calculate hit rate from API response data
      const hitRate = apiStats.totalFound > 0 ? (apiStats.totalQuantityOfSales / apiStats.totalFound).toFixed(2) : 0;
      
      return {
        totalNoOfSearch: apiStats.totalNoOfSearch,
        totalFound: apiStats.totalFound,
        totalNotFound: apiStats.totalNotFound,
        totalQuantityOfSales: apiStats.totalQuantityOfSales,
        hitRate: hitRate
      };
    }
    
    // Return zeros if no API data yet
    return {
      totalNoOfSearch: 0,
      totalFound: 0,
      totalNotFound: 0,
      totalQuantityOfSales: 0,
      hitRate: 0
    };
  };

  // Add data summary for client-side pagination
  const renderDataSummary = () => {
    if (totalRecords === 0) return null;
    
    return (
      <div className="data-summary mb-3 p-3 bg-light rounded">
        <div className="row">
          <div className="col-md-3">
            <strong>Total Records:</strong> {totalRecords.toLocaleString()}
          </div>
          <div className="col-md-3">
            <strong>Current Page:</strong> {currentPage} of {totalPages}
          </div>
          <div className="col-md-3">
            <strong>Records per Page:</strong> {itemsPerPage}
          </div>
          <div className="col-md-3">
            <strong>Showing:</strong> {Math.min(itemsPerPage, currentData.length)} records
          </div>
        </div>

      </div>
    );
  };

  const stats = useMemo(() => calculateStatistics(), [demandData]);

  if (loading) {
    return (
      <div style={{
        position: 'fixed',   
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(255,255,255,0.8)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px'
      }}>
        <div className="bouncing-loader">
          <div></div><div></div><div></div>
        </div>
        <p style={{ 
          color: '#333', 
          fontSize: '18px', 
          fontWeight: '600',
          margin: 0,
          textAlign: 'center',
          textShadow: '0 1px 2px rgba(0,0,0,0.1)'
        }}>
          Data is loading, please wait...
        </p>
        <style>
          {`
          .bouncing-loader {
            display: flex;
            align-items: flex-end;
            justify-content: center;
            height: 60px;
            gap: 10px;
          }
          .bouncing-loader div {
            width: 18px;
            height: 18px;
            background: linear-gradient(135deg, #ff6600 40%, #ffa500 100%);
            border-radius: 50%;
            animation: bouncing-loader 0.6s infinite alternate;
            box-shadow: 0 4px 16px rgba(255, 102, 0, 0.2);
          }
          .bouncing-loader div:nth-child(2) {
            animation-delay: 0.2s;
          }
          .bouncing-loader div:nth-child(3) {
            animation-delay: 0.4s;
          }
          @keyframes bouncing-loader {
            to {
              transform: translateY(-30px) scale(1.1);
              box-shadow: 0 16px 32px rgba(255, 102, 0, 0.3);
            }
          }
          `}
        </style>
      </div>
    );
  }

  return (
    <div className="">
      <div className="filter_section d-flex align-items-start row mb_25">
        <div className="filter_items col-md-3 m-0">
          <label className="font_14 fw-med text_black positoin-relative">
            Part Type
            {!selectedPartTypes.includes('All') && selectedPartTypes.length > 0 && (
              <span className="badge bg-primary ms-2 badge_container" style={{ fontSize: '10px' }}>
                {selectedPartTypes.length} selected
              </span>
            )}
          </label>
          <Multiselect
            isObject={false}
            options={getPartTypeOptions()}
            selectedValues={selectedPartTypes}
            onSelect={(selectedList, selectedItem) => {
              handleSelect(selectedList, selectedItem, setSelectedPartTypes);
            }}
            onRemove={(selectedList, removedItem) => {
              handleRemove(selectedList, removedItem, setSelectedPartTypes, getPartTypeOptions());
            }}
            placeholder="Select Part Type"
          />
        </div> 
        
        <div className="filter_items col-md-3 m-0">
          <label className="font_14 fw-med text_black positoin-relative">
            Region
            {!selectedRegions.includes('All') && selectedRegions.length > 0 && (
              <span className="badge bg-primary ms-2 badge_container" style={{ fontSize: '10px' }}>
                {selectedRegions.length} selected
              </span>
            )}
          </label>
          <Multiselect
            isObject={false}
            options={regions && regions.length > 0 ? regions : ['All']}
            selectedValues={selectedRegions}
            onSelect={(selectedList, selectedItem) => {
              handleSelect(selectedList, selectedItem, setSelectedRegions);
            }}
            onRemove={(selectedList, removedItem) => {
              handleRemove(selectedList, removedItem, setSelectedRegions, regions);
            }}
            placeholder="Select Region"
          />
        </div>
         
        <div className="filter_items col-md-3 m-0">
          <label className="font_14 fw-med text_black positoin-relative">
            IC Number
            {!selectedInterchangeNumbers.includes('All') && selectedInterchangeNumbers.length > 0 && (
              <span className="badge bg-primary ms-2 badge_container" style={{ fontSize: '10px' }}>
                {selectedInterchangeNumbers.length} selected
              </span>
            )}
          </label>
          <Multiselect
            isObject={false}
            options={interchangeNumbers && interchangeNumbers.length > 0 ? interchangeNumbers : ['All', '10007', '10008', '10009']}
            selectedValues={selectedInterchangeNumbers}
            onSelect={(selectedList, selectedItem) => {
              handleSelect(selectedList, selectedItem, setSelectedInterchangeNumbers);
            }}
            onRemove={(selectedList, removedItem) => {
              handleRemove(selectedList, removedItem, setSelectedInterchangeNumbers, interchangeNumbers);
            }}
            placeholder="Select IC Number"
          />
        </div>
        <div className="filter_items col-md-3 m-0">
          <label className="font_14 fw-med text_black">
            Start Date
          </label>
          <input
            type="date"
            className="form-control"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
                    
        </div>
        <div className="filter_items col-md-3 m-0">
          <label className="font_14 fw-med text_black">
            End Date
          </label>
          <input
            type="date"
            className="form-control"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
                     
        </div>
        <div className="filter_button_blk mt_26 ml_10">
          <img 
            src={searchIcon} 
            alt="search" 
            className="" 
            onClick={handleSearch}
            style={{ 
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
            onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
            onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
          />
        </div>
      </div>
      <div className="row report_container demand_report_blk mb_10">
        <div className="col-md-3">
          <div className="report_info_blks">
            <span className="font_14 fw-reg text_black mb_10 d-block">Total no.of Search</span>
            <h4 className="font_24 fw-semi text_black">{stats.totalNoOfSearch.toLocaleString('en-IN')}</h4>
          </div>
        </div>
        <div className="col-md-3">
          <div className="report_info_blks">
            <span className="font_14 fw-reg text_black mb_10 d-block">Total Found</span>
            <h4 className="font_24 fw-semi text_black">{stats.totalFound.toLocaleString('en-IN')}</h4>
          </div>
        </div>
        <div className="col-md-3">
          <div className="report_info_blks">
            <span className="font_14 fw-reg text_black mb_10 d-block">Total Not found</span>
            <h4 className="font_24 fw-semi text_black">{stats.totalNotFound.toLocaleString('en-IN')}</h4>
          </div>
        </div>
        <div className="col-md-3">
          <div className="report_info_blks">
            <span className="font_14 fw-reg text_black mb_10 d-block">Total Quantity Of Sales</span>
            <h4 className="font_24 fw-semi text_black">{stats.totalQuantityOfSales.toLocaleString('en-IN')}</h4>
          </div>
        </div>
        <div className="col-md-3">
          <div className="report_info_blks">
            <span className="font_14 fw-reg text_black mb_10 d-block">Close Rate</span>
            <h4 className="font_24 fw-semi text_black">{stats.hitRate}</h4>
          </div>
        </div>
      </div>
      <div className="p-0 report_table_bg">
        {/* Data Summary for Large Datasets */}
        {/* {renderDataSummary()} */}
        
        {/* <h4 className="font_18 fw-semi mb_10">Demand Data</h4> */}
        <table className="w-100 report_table">
          <thead>
            <tr>
               <th>Top 20 Searches By Customer</th>
               <th>Top 20 Sales By Customer</th>
              <th>Top 20 Searches By IC-Number</th>
              <th>Top 20 Sales By IC-Number</th>
              
            </tr>
          </thead>
          <tbody>
            {demandData.length > 0 && demandData[0] ? (
              <tr>
                <td className="p-0">
                  <div className="top20-list p-0">
                    <div className="table-column-headers">
                      <span className="column-header p-0">Customer Code</span>
                      <span className="column-header p-0">Count</span>
                    </div>
                    {demandData[0].top20searchByCustomerArray && demandData[0].top20searchByCustomerArray.length > 0 ? (
                      demandData[0].top20searchByCustomerArray.map((item, index) => (
                        <div key={index} className="list-item">
                          <span className="item-number">{index + 1}.</span>
                          <span className="item-value">{item.value ? item.value.toLocaleString() : 'N/A'}</span>
                          <span className="item-count">({item.count.toLocaleString()})</span>
                        </div>
                      ))
                    ) : (
                      <div className="no-data-message">No data found</div>
                    )}
                  </div>
                </td>
                <td className="p-0">
                  <div className="top20-list p-0">
                    <div className="table-column-headers">
                      <span className="column-header p-0">Customer Code</span>
                      <span className="column-header p-0">Count</span>
                    </div>
                    {demandData[0].top20SalesByCustomerArray && demandData[0].top20SalesByCustomerArray.length > 0 ? (
                      demandData[0].top20SalesByCustomerArray.map((item, index) => (
                        <div key={index} className="list-item">
                          <span className="item-number">{index + 1}.</span>
                          <span className="item-value">{item.value ? item.value.toLocaleString() : 'N/A'}</span>
                          <span className="item-count">({item.count.toLocaleString()})</span>
                        </div>
                      ))
                    ) : (
                      <div className="no-data-message">No data found</div>
                    )}
                  </div>
                </td>
                <td className="p-0">
                  <div className="top20-list p-0">
                    <div className="table-column-headers">
                      <span className="column-header p-0">IC Number</span>
                      <span className="column-header p-0">Count</span>
                    </div>
                    {demandData[0].top20searchByICnumberArray && demandData[0].top20searchByICnumberArray.length > 0 ? (
                      demandData[0].top20searchByICnumberArray.map((item, index) => (
                        <div key={index} className="list-item">
                          <span className="item-number">{index + 1}.</span>
                          <span className="item-value">{item.value ? item.value.toLocaleString() : 'N/A'}</span>
                          <span className="item-count">({item.count.toLocaleString()})</span>
                        </div>
                      ))
                    ) : (
                      <div className="no-data-message">No data found</div>
                    )}
                  </div>
                </td>
                
                <td className="p-0">
                  <div className="top20-list p-0" >
                    <div className="table-column-headers">
                      <span className="column-header p-0">IC Number</span>
                      <span className="column-header p-0">Count</span>
                    </div>
                    {demandData[0].top20SalesByICArray && demandData[0].top20SalesByICArray.length > 0 ? (
                      demandData[0].top20SalesByICArray.map((item, index) => (
                        <div key={index} className="list-item">
                          <span className="item-number">{index + 1}.</span>
                          <span className="item-value">{item.value ? item.value.toLocaleString() : 'N/A'}</span>
                          <span className="item-count">({item.count.toLocaleString()})</span>
                        </div>
                      ))
                    ) : (
                      <div className="no-data-message">No data found</div>
                    )}
                  </div>
                </td>
                
              </tr>
            ) : (
              <tr>
                <td colSpan="4" className="no-data-row">
                  <div className="no-data-message">No data found</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        
        {/* Pagination Controls */}
        {renderPagination()}
      </div>
    </div>
  );
};

export default DemandReport;
